import * as React from 'react';
import { MapModal } from '../Components/MapModal/MapModal';
import styles from './InstallModal.module.css';
import { AnimatedButton } from '../AnimatedButton';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useSelector } from 'react-redux';

interface InstallModalProps {
  onClose: () => void;
  deferredPrompt: Event | null;
  isIOS: boolean;
  isPWAInstalled: boolean;
  isStandalone: boolean;
  appIcon?: string; // URL to the app icon
  appTitle?: string; // Title of the app
  downloadProgress?: number;
}

const IOSInstructions: React.FC<{ appIcon?: string; appTitle?: string }> = ({
  appIcon,
  appTitle,
}) => {
  const [stage, setStage] = React.useState<'description' | 'instructions'>('description');

  if (stage === 'description') {
    return (
      <div>
        <h2 className={styles.title}>Install for Offline Use</h2>
        <div className={styles.message}>
          <p>
            This app can be installed on your device to use offline. This means you can use the app
            even when you don&apos;t have internet connection.
          </p>
        </div>
        <AnimatedButton
          className={styles.installButton}
          clickedClassName={styles.clicked}
          onClick={() => setStage('instructions')}
        >
          Continue
        </AnimatedButton>
      </div>
    );
  }

  return (
    <div>
      <h2 className={styles.title}>How to install on your iPhone</h2>
      <div className={styles.message} style={{ marginBottom: 0 }}>
        <ol>
          <li>
            <strong>Step 1:</strong> Tap the Share button at the bottom of the screen
            <div className={styles.iconContainer}>
              <img
                src="/static/images/map/ios-share-icon.svg"
                alt="iOS Share Button"
                className={styles.shareButtonImage}
              />
            </div>
          </li>
          <li>
            <strong>Step 2:</strong> Scroll down and tap &quot;Add to Home Screen&quot;
            <div className={styles.iconContainer}>
              <img
                src="/static/images/map/ios-add-home.svg"
                alt="Add to Home Screen option"
                className={styles.addHomeImage}
              />
            </div>
          </li>
          <li>
            <strong>Step 3:</strong> Tap &quot;Add&quot; in the top right to install
            <div className={styles.iconContainer}>
              <img
                src="/static/images/map/ios-add-confirm.svg"
                alt="Tap Add to confirm"
                className={styles.confirmImage}
              />
            </div>
          </li>
          <li>
            <strong>Step 4:</strong> Open the app from your home screen
            <div className={styles.iconContainer}>
              <div className={styles.homeScreenIcon}>
                {appIcon && (
                  <img src={appIcon} alt={`${appTitle || 'App'} icon`} className={styles.appIcon} />
                )}
                <div className={styles.appTitle}>{appTitle || 'App'}</div>
              </div>
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
};

interface AndroidInstructionsProps {
  deferredPrompt: Event | null;
}

const AndroidInstructions: React.FC<AndroidInstructionsProps> = ({ deferredPrompt }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      setIsLoading(true);
      await (deferredPrompt as any).prompt();
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className={styles.title}>Install for Offline Use</h2>
      <div className={styles.message}>
        <p>
          This app can be installed on your device to use offline. This means you can use the app
          even when you don&apos;t have internet connection.
        </p>
      </div>
      <AnimatedButton
        className={styles.installButton}
        onClick={handleInstallClick}
        disabled={!deferredPrompt || isLoading}
        clickedClassName={styles.clicked}
      >
        {isLoading ? 'Installing...' : 'Install'}
      </AnimatedButton>
    </div>
  );
};

const AlreadyInstalledStandalone: React.FC<{ downloadProgress: number | undefined }> = ({
  downloadProgress,
}) => {
  // Existing download in progress or success message
  return downloadProgress === 100 ? (
    <div className={styles.instructions}>
      <h2 className={styles.title}>Successfully Installed</h2>
      <p className={styles.message}>
        You have successfully installed the app and are ready to use it offline.
      </p>
    </div>
  ) : (
    <div className={styles.instructions}>
      <h2 className={styles.title}>Downloading Content...</h2>
      <p className={styles.message}>Please wait while we download the content for offline use.</p>
    </div>
  );
};

const AlreadyInstalledBrowser: React.FC = () => (
  <div className={styles.instructions}>
    <h2 className={styles.title}>Already Installed</h2>
    <p className={styles.message}>
      This app is already installed on your device. Please open the app from your home screen to
      ensure that you will be able to use it offline.
    </p>
  </div>
);

export const InstallModal: React.FC<InstallModalProps> = ({
  onClose,
  deferredPrompt,
  isIOS,
  isPWAInstalled,
  isStandalone,
  downloadProgress,
}) => {
  const digitalMap = useSelector(selectDigitalMap);
  const appIcon = digitalMap?.pwa_settings?.icon_url_512;
  const appTitle = digitalMap?.pwa_settings?.title;

  const renderContent = () => {
    if (isStandalone) {
      return <AlreadyInstalledStandalone downloadProgress={downloadProgress} />;
    }

    if (isPWAInstalled) {
      return <AlreadyInstalledBrowser />;
    }

    if (isIOS) {
      return <IOSInstructions appIcon={appIcon} appTitle={appTitle} />;
    }

    return <AndroidInstructions deferredPrompt={deferredPrompt} />;
  };

  return <MapModal onClose={onClose}>{renderContent()}</MapModal>;
};
